import styled from '@emotion/styled'
import {
  TableRowProps,
  TableHeadProps,
  TableRow,
  TableHead,
  TableCell,
  TableSortLabel,
} from '@mui/material'
import { theme } from 'assets/styles/customTheme'

export type Order = 'asc' | 'desc'

export interface HeadCell extends TableHeadProps {
  label: string | JSX.Element
  numeric: boolean
  sortable?: boolean
  hidden?: boolean
}

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  // Extract tag from React element if present
  const getTagValue = (val: any): string => {
    // Special handling for React elements with tags property
    if (val && typeof val === 'object' && val.props && val.props.tags) {
      // Convert tags array to string for comparison
      const tags = val.props.tags
      if (Array.isArray(tags) && tags.length > 0) {
        return String(tags[0] || '')
      }
      return ''
    }

    // Handle non-React objects
    if (val && typeof val === 'object') {
      return String(val)
    }

    // Handle primitives
    return String(val || '')
  }

  // Get comparison values and convert to strings
  const aVal = getTagValue(a[orderBy])
  const bVal = getTagValue(b[orderBy])

  // Perform case-insensitive string comparison
  if (typeof aVal === 'string' && typeof bVal === 'string') {
    return bVal.toLowerCase().localeCompare(aVal.toLowerCase())
  }

  // Fallback to regular comparison
  return bVal < aVal ? -1 : bVal > aVal ? 1 : 0
}

export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
  return (a, b) => {
    // Special handling for tags column
    if (orderBy === 'tags') {
      const getTagValue = (val: any): string => {
        if (val && typeof val === 'object' && val.props && val.props.tags) {
          const tags = val.props.tags
          if (Array.isArray(tags) && tags.length > 0) {
            return String(tags[0] || '')
          }
          return ''
        }
        return String(val || '')
      }

      const aVal = getTagValue(a[orderBy])
      const bVal = getTagValue(b[orderBy])

      // If either value is empty/null, handle specially
      const aEmpty = !aVal
      const bEmpty = !bVal

      if (aEmpty && !bEmpty) return 1 // Empty values always go to bottom
      if (!aEmpty && bEmpty) return -1 // Empty values always go to bottom
      if (aEmpty && bEmpty) return 0 // Both empty, maintain relative position

      // For non-empty values, sort according to order
      return order === 'desc'
        ? bVal.toLowerCase().localeCompare(aVal.toLowerCase())
        : aVal.toLowerCase().localeCompare(bVal.toLowerCase())
    }

    // For all other columns, use the regular comparator
    return order === 'desc'
      ? descendingComparator(a, b, orderBy)
      : -descendingComparator(a, b, orderBy)
  }
}

export interface EnhancedTableProps {
  tableHeaders: HeadCell[]
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof TableRowProps
  ) => void
  order: Order
  orderBy: string
  disableSorting?: boolean
  enableCheckboxes?: boolean
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void
  numSelected?: number
  rowCount?: number
}

export const TableHeader = ({
  tableHeaders,
  onRequestSort,
  order,
  orderBy,
  disableSorting,
  enableCheckboxes,
  onSelectAllClick,
  numSelected = 0,
  rowCount = 0,
}: EnhancedTableProps) => {
  const createSortHandler =
    (property: keyof TableRowProps) => (event: React.MouseEvent<unknown>) => {
      if (!disableSorting) {
        onRequestSort(event, property)
      }
    }
  const CustomHeader = styled(TableRow)({
    '&.MuiTableRow-root': {
      backgroundColor: theme.palette.grayscale.hover,
    },
  })

  return (
    <TableHead>
      <CustomHeader>
        {enableCheckboxes && (
          <TableCell
            padding="checkbox"
            sx={{ backgroundColor: 'transparent' }}
          />
        )}
        {tableHeaders?.map((headCell: HeadCell) => {
          // Skip rendering hidden columns
          if (headCell.hidden) return null

          // Skip rendering columns used only for sorting
          if (headCell.id === 'tagSortValue') {
            // Find the tag header to reuse its label
            const tagHeader = tableHeaders.find((h) => h.id === 'tags')
            if (tagHeader) {
              headCell = { ...headCell, label: tagHeader.label }
            }
          }

          return (
            <TableCell
              sx={{ ...headCell.sx, backgroundColor: 'transparent' }}
              key={headCell.id}
              align={'left'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.sortable !== false && !disableSorting ? (
                <TableSortLabel
                  hideSortIcon={disableSorting}
                  active={orderBy === headCell.id && !disableSorting}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(
                    headCell.id as keyof TableRowProps
                  )}
                  sx={{
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    '& .MuiTableSortLabel-icon, &.Mui-active': {
                      color: `${theme.palette.primary.main} !important`,
                    },
                  }}
                >
                  {headCell.label}
                </TableSortLabel>
              ) : (
                headCell.label
              )}
            </TableCell>
          )
        })}
      </CustomHeader>
    </TableHead>
  )
}
